import Hotjar from "@hotjar/browser";
import { fraudBrowserSDK } from "@mozn-sdk/focal-fraud";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React from "react";
import ReactDOM from "react-dom/client";
import {
	createBrowserRouter,
	createRoutesFromChildren,
	createRoutesFromElements,
	matchRoutes,
	Route,
	RouterProvider,
	useLocation,
	useNavigationType,
} from "react-router-dom";
import { Userpilot } from "userpilot";
import packageJson from "../package.json";
import { initAuth } from "./auth";
import {
	BROWSER_FINGERPRINT_SDK_API_KEY,
	BROWSER_FINGERPRINT_SDK_API_URL,
	IS_DEVELOPMENT,
	IS_ON_PREMISE,
	IS_POC,
	IS_PRODUCTION,
	NODE_ENV,
	SENTRY_DNS,
} from "./constants/constants";
import { getFliptFlag } from "./helpers/get-flipt-flag";
import Main from "./modules/main";
import "./assets/scss/main.scss";
import "./assets/scss/tailwind.scss";
import "@moznsystems/mozn-ui/dist/mozn-ui.cjs.development.css";
import { UserData } from "./modules/main/main.types";

if (!IS_ON_PREMISE || !IS_POC) {
	const siteId = parseInt(process.env.VITE_APP_HOTJAR_SITE_ID || "0");
	const hotjarVersion = 6;
	Hotjar.init(siteId, hotjarVersion);
	Userpilot.initialize(process.env.VITE_APP_USERPILOT_TOKEN as string);
}

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnReconnect: false,
			refetchOnWindowFocus: false,
			retry: false,
		},
	},
});

(async () => {
	const auth = await initAuth();
	const user = auth.getUser();

	if (user) {
		const fingerprintBrowserSDK = await getFliptFlag({
			flagKey: "browser-fingerprint-sdk",
		});

		if (fingerprintBrowserSDK.enabled) {
			fraudBrowserSDK({
				apiKey: BROWSER_FINGERPRINT_SDK_API_KEY!,
				apiUrl: BROWSER_FINGERPRINT_SDK_API_URL!,
				userId: user.sub,
				hashTimezone: IS_PRODUCTION,
				excludeSignals: IS_PRODUCTION ? ["currentLocation"] : [],
			});
		}

		Userpilot.identify(user.sub as string, {
			name: `${user.given_name} ${user.family_name}`,
			email: user.email,
			company: {
				id: user.tenant_id,
				name: user.tenant_name,
			},
			created_at: user.created_timestamp,
		});

		if (!IS_DEVELOPMENT && (!IS_ON_PREMISE || !IS_POC)) {
			Sentry.init({
				integrations: [
					Sentry.reactRouterV6BrowserTracingIntegration({
						useEffect: React.useEffect,
						useLocation,
						useNavigationType,
						createRoutesFromChildren,
						matchRoutes,
					}),
				],
				tracesSampleRate: 1.0,
				dsn: SENTRY_DNS,
				release: packageJson?.version || "1.0.0",
				environment: NODE_ENV,
			});

			Sentry.setUser({
				email: user.email,
			});
		}

		const router = createBrowserRouter(
			createRoutesFromElements(
				<Route path="/*" element={<Main userData={user as UserData} />} />,
			),
		);

		ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
			<QueryClientProvider client={queryClient}>
				<RouterProvider router={router} />
			</QueryClientProvider>,
		);
	}
})();
